/**
 * Suggestion_Service.ts
 * AI suggestion lifecycle
 * (sk-catalog-api POST /api/v1/items/:id/suggestions, /api/v1/suggestions/*)
 */

import axiosInstance from '../../api/axiosInstance';
import {
  Catalog_Item_Suggestions_Url,
  Catalog_Suggestions_Url,
  Catalog_Suggestion_Url,
  Catalog_Suggestion_Accept_Url,
  Catalog_Suggestion_Reject_Url,
} from '../../api/endpoints';
import {
  CreateSuggestionPayload,
  Suggestion,
  SuggestionResponse,
  SuggestionsResponse,
} from '../../schemas/catalog/suggestion';

/* ─────────────────────────────────────────
   Create — POST /api/v1/items/:id/suggestions
   AI authoring (suggestions:write); creates a staged PENDING suggestion.
───────────────────────────────────────── */

export const createSuggestion = async (
  itemId: string,
  payload: CreateSuggestionPayload,
): Promise<SuggestionResponse> => {
  const { data } = await axiosInstance.post<SuggestionResponse>(
    Catalog_Item_Suggestions_Url(itemId),
    payload,
  );
  return data;
};

/* ─────────────────────────────────────────
   List review queue — GET /api/v1/suggestions
───────────────────────────────────────── */

export const getSuggestions = async (
  params?: { catalogItemId?: string; status?: 'PENDING' | 'ACCEPTED' | 'REJECTED'; kind?: string; limit?: number },
): Promise<SuggestionsResponse> => {
  const { data } = await axiosInstance.get<SuggestionsResponse>(Catalog_Suggestions_Url, { params });
  return data;
};

/* ─────────────────────────────────────────
   Get one — GET /api/v1/suggestions/:id
───────────────────────────────────────── */

export const getSuggestion = async (id: string): Promise<SuggestionResponse> => {
  const { data } = await axiosInstance.get<SuggestionResponse>(Catalog_Suggestion_Url(id));
  return data;
};

/* ─────────────────────────────────────────
   Accept — POST /api/v1/suggestions/:id/accept
   Human accepts → field value goes live (suggestions:review).
───────────────────────────────────────── */

export const acceptSuggestion = async (id: string): Promise<SuggestionResponse> => {
  const { data } = await axiosInstance.post<SuggestionResponse>(Catalog_Suggestion_Accept_Url(id));
  return data;
};

/* ─────────────────────────────────────────
   Reject — POST /api/v1/suggestions/:id/reject
   Human rejects → dismissed, no field mutation.
───────────────────────────────────────── */

export const rejectSuggestion = async (id: string, note?: string): Promise<SuggestionResponse> => {
  const { data } = await axiosInstance.post<SuggestionResponse>(
    Catalog_Suggestion_Reject_Url(id),
    note ? { note } : {},
  );
  return data;
};

export type { Suggestion };
